import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { track } from "@/lib/analytics";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { StoreProvider, useStore } from "@/context/StoreContext";
import Layout from "@/components/Layout";
import { Spinner } from "@/components/shared";

import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Consignors from "@/pages/Consignors";
import ConsignorDetail from "@/pages/ConsignorDetail";
import Items from "@/pages/Items";
import Sales from "@/pages/Sales";
import Settlements from "@/pages/Settlements";
import SettlementDetail from "@/pages/SettlementDetail";
import Statement from "@/pages/Statement";
import Expiring from "@/pages/Expiring";
import Reports from "@/pages/Reports";
import Integrations from "@/pages/Integrations";
import Settings from "@/pages/Settings";
import Import from "@/pages/Import";
import Billing from "@/pages/Billing";
import ShopifyEntry from "@/pages/ShopifyEntry";
import Traffic from "@/pages/Traffic";
import Home from "@/pages/Home";

function PageTracker() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    if (window.__coLastPath === pathname) return;
    window.__coLastPath = pathname;
    track("pageview", pathname);
  }, [pathname]);
  return null;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-[#faf9f5]"><Spinner label="Loading…" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return <StoreGate>{children}</StoreGate>;
}

function StoreGate({ children }) {
  const { store, loading } = useStore();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#faf9f5]"><Spinner /></div>;
  if (!store) return <Navigate to="/onboarding" replace />;
  return <Layout>{children}</Layout>;
}

function OnboardingGate() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-[#faf9f5]"><Spinner /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <Onboarding />;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-[#faf9f5]"><Spinner /></div>;
  if (user && user.onboarded) return <Navigate to="/dashboard" replace />;
  if (user && !user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <StoreProvider>
          <Toaster position="top-right" richColors />
          <PageTracker />
          <Routes>
            <Route path="/" element={<PublicOnly><Home /></PublicOnly>} />
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/onboarding" element={<OnboardingGate />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/consignors" element={<Protected><Consignors /></Protected>} />
            <Route path="/consignors/:id" element={<Protected><ConsignorDetail /></Protected>} />
            <Route path="/items" element={<Protected><Items /></Protected>} />
            <Route path="/sales" element={<Protected><Sales /></Protected>} />
            <Route path="/settlements" element={<Protected><Settlements /></Protected>} />
            <Route path="/settlements/:id" element={<Protected><SettlementDetail /></Protected>} />
            <Route path="/statement/:lineId" element={<Protected><Statement /></Protected>} />
            <Route path="/expiring" element={<Protected><Expiring /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/integrations" element={<Protected><Integrations /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/import" element={<Protected><Import /></Protected>} />
            <Route path="/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/shopify" element={<ShopifyEntry />} />
            <Route path="/traffic" element={<Protected><Traffic /></Protected>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </StoreProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
